/**
 * Open-redirect guard for post-auth `?next=` redirects.
 *
 * After login / sign-up we return the user to the path they were headed to, carried in a `?next=`
 * query param. Unchecked, that's an open-redirect: a crafted `?next=//evil.com` would bounce a
 * freshly-authenticated user off-site. `safeNext` reduces any value to a trusted same-origin path.
 *
 * It is written to be safe on its own — it does not assume the caller has decoded the value first.
 */

/**
 * Resolve a `?next=` value to a safe same-origin path, or `/home` if it can't be trusted.
 *
 * Blocks off-site targets — protocol-relative (`//evil.com`), schemes (`https://…`, `javascript:`),
 * backslash tricks (`/\evil.com`), and encoded separators (`/%2F/evil.com`) — while letting plain
 * in-app paths through (`/home`, `/claim/maintain?x=1`, …).
 */
export function safeNext(next: string | null | undefined): string {
  const FALLBACK = "/home";
  if (!next) return FALLBACK;

  // An encoded slash/backslash can decode to "//" (→ off-site) downstream; real in-app paths don't
  // contain one, so reject outright.
  if (/%2f|%5c/i.test(next)) return FALLBACK;

  // Resolve against a throwaway origin. Anything that escapes it — a scheme, a protocol-relative
  // "//", a normalised backslash — resolves to a different or opaque origin and is rejected.
  try {
    const base = "https://x.invalid";
    const url = new URL(next, base);
    if (url.origin !== base) return FALLBACK;

    // Never send a freshly-authenticated user "back" to an auth page. Besides being
    // pointless, it can loop: the auth pages redirect an already-signed-in visitor via
    // safeNext(next), so a next of "/login" would bounce to itself forever.
    if (/^\/(login|register|forgot-password)(\/|$|\?)/i.test(url.pathname)) return FALLBACK;

    return url.pathname + url.search + url.hash;
  } catch {
    return FALLBACK;
  }
}
